import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  ChevronLeft,
  FileText,
  Image as ImageIcon,
  KeyRound,
  Layers,
  ListTree,
  Map,
  Radio,
  Shield,
  Sparkles,
  Wrench,
} from "lucide-react";

import { PAD2 } from "../data/pad2";
import { StatusBadge } from "../components/StatusBadge";
import {
  MockApiSnippets,
  MockAuditPack,
  MockCodeplugDiff,
  MockComplianceCal,
  MockCompliance,
  MockCoveragePredictor,
  MockCrewTracking,
  MockDrPlaybook,
  MockFieldPhotos,
  MockHealthScore,
  MockHeatmapViewer,
  MockLogTail,
  MockLossBudget,
  MockManDownGeofence,
  MockR7Provision,
  MockSecretVault,
  MockSelfService,
  MockSiteComparison,
  MockSlackBot,
  MockSpotifyWrapped,
  MockSshTerminal,
  MockTopology,
} from "../components/teasers/Teasers";

type Tab =
  | "overview"
  | "hardware"
  | "codeplugs"
  | "frequencies"
  | "secrets"
  | "heatmaps"
  | "layouts"
  | "photos"
  | "activity"
  | "crew";

const TABS: { id: Tab; label: string; icon: React.ReactNode }[] = [
  { id: "overview", label: "Overview", icon: <Layers className="h-3.5 w-3.5" /> },
  { id: "hardware", label: "Hardware", icon: <Radio className="h-3.5 w-3.5" /> },
  { id: "codeplugs", label: "Codeplugs", icon: <FileText className="h-3.5 w-3.5" /> },
  { id: "frequencies", label: "Frequencies", icon: <Sparkles className="h-3.5 w-3.5" /> },
  { id: "secrets", label: "Secrets", icon: <KeyRound className="h-3.5 w-3.5" /> },
  { id: "heatmaps", label: "Heatmaps", icon: <Map className="h-3.5 w-3.5" /> },
  { id: "layouts", label: "Layouts", icon: <Layers className="h-3.5 w-3.5" /> },
  { id: "photos", label: "Photos", icon: <ImageIcon className="h-3.5 w-3.5" /> },
  { id: "activity", label: "Activity", icon: <ListTree className="h-3.5 w-3.5" /> },
  { id: "crew", label: "Crew", icon: <Wrench className="h-3.5 w-3.5" /> },
];

export function SiteDetail() {
  const { code } = useParams<{ code: string }>();
  const [tab, setTab] = useState<Tab>("overview");

  // Phase 1: GET /api/v1/dossier/sites/:code — für MVP nur PAD2 statisch
  if (code !== "PAD2") {
    return <NonPad2Placeholder code={code ?? "?"} />;
  }

  const site = PAD2;
  const onlineRepeaters = site.repeaters.filter((r) => r.online).length;
  const totalRepeaters = site.repeaters.length;
  const codeplugDrift = site.codeplugs.filter((c) => !c.is_standard && !c.is_deployed).length;

  return (
    <div className="p-4 sm:p-6 space-y-4 max-w-7xl">
      {/* Breadcrumb */}
      <Link
        to="/"
        className="inline-flex items-center gap-1 text-xs font-mono text-zinc-500 hover:text-zinc-300"
      >
        <ChevronLeft className="h-3 w-3" />
        all sites
      </Link>

      {/* Header */}
      <header className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-4">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <div className="flex items-baseline gap-3">
              <h1 className="text-2xl font-bold font-mono">{site.code}</h1>
              <span className="text-zinc-400">{site.name.replace(`${site.code} — `, "")}</span>
              <StatusBadge label={site.business_unit} tone="info" />
            </div>
            <div className="mt-1.5 text-xs font-mono text-zinc-500">
              {site.address} · {site.coordinates.lat}°N {site.coordinates.lon}°E
            </div>
            <div className="mt-1 text-xs text-zinc-500">
              PoC Spreenauten: <span className="text-zinc-300">{site.poc_spreenauten}</span>
              {" · "}PoC Amazon: <span className="text-zinc-300">{site.poc_amazon}</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-1.5 items-start">
            <StatusBadge
              label={`Repeaters ${onlineRepeaters}/${totalRepeaters}`}
              tone={onlineRepeaters === totalRepeaters ? "ok" : "err"}
            />
            <StatusBadge label="Freq valid until 2027-08-12" tone="ok" />
            <StatusBadge label={`Codeplug drift: ${codeplugDrift}`} tone={codeplugDrift > 0 ? "warn" : "ok"} />
            <StatusBadge label="Last survey: 2025-06-17 (340d)" tone="warn" />
            <StatusBadge label="AES rotation due: 2026-08-12" tone="ok" />
          </div>
        </div>
      </header>

      {/* Tab nav */}
      <nav className="flex flex-wrap items-center gap-1 border-b border-zinc-800">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-mono uppercase tracking-wide border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? "border-emerald-400 text-zinc-100"
                : "border-transparent text-zinc-500 hover:text-zinc-300"
            }`}
          >
            {t.icon}
            {t.label}
          </button>
        ))}
      </nav>

      {/* Tab content */}
      {tab === "overview" && <OverviewTab />}
      {tab === "hardware" && <HardwareTab />}
      {tab === "codeplugs" && <CodeplugsTab />}
      {tab === "frequencies" && <FrequenciesTab />}
      {tab === "secrets" && <SecretsTab />}
      {tab === "heatmaps" && <HeatmapsTab />}
      {tab === "layouts" && <LayoutsTab />}
      {tab === "photos" && <PhotosTab />}
      {tab === "activity" && <ActivityTab />}
      {tab === "crew" && <CrewTab />}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Tabs
// ─────────────────────────────────────────────────────────────────────────────

function OverviewTab() {
  return (
    <div className="grid lg:grid-cols-2 gap-3">
      <MockTopology />
      <MockHealthScore />
      <MockCompliance />
      <MockCodeplugDiff />
      <MockApiSnippets />
      <MockDrPlaybook />
    </div>
  );
}

function HardwareTab() {
  const site = PAD2;
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 overflow-hidden">
        <div className="px-3 py-2 text-xs font-mono uppercase tracking-widest text-zinc-400 border-b border-zinc-800 flex items-center gap-2">
          <Radio className="h-3.5 w-3.5" />
          Repeater Cluster · {site.repeaters.length} units · IPSC system code 8
        </div>
        <div className="overflow-auto">
          <table className="w-full text-xs">
            <thead className="bg-zinc-950/60 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
              <tr>
                <th className="text-left px-3 py-2">Asset</th>
                <th className="text-left px-3 py-2">Role</th>
                <th className="text-left px-3 py-2">Model / SN</th>
                <th className="text-left px-3 py-2">Firmware</th>
                <th className="text-left px-3 py-2">IP : UDP</th>
                <th className="text-left px-3 py-2">RID</th>
                <th className="text-left px-3 py-2">Rx / Tx (MHz)</th>
                <th className="text-left px-3 py-2">Slot</th>
                <th className="text-left px-3 py-2">PA °C</th>
                <th className="text-left px-3 py-2">VSWR</th>
                <th className="text-left px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-900 font-mono">
              {site.repeaters.map((r) => (
                <tr key={r.id} className="hover:bg-zinc-900/40">
                  <td className="px-3 py-1.5 text-zinc-200 font-semibold">{r.id}</td>
                  <td className={`px-3 py-1.5 ${r.role === "master" ? "text-emerald-300" : "text-zinc-400"}`}>{r.role}</td>
                  <td className="px-3 py-1.5 text-zinc-400">{r.model} · {r.serial}</td>
                  <td className="px-3 py-1.5 text-zinc-400">{r.firmware}</td>
                  <td className="px-3 py-1.5 text-zinc-400">{r.ip}:{r.udp_port}</td>
                  <td className="px-3 py-1.5">{r.radio_id}</td>
                  <td className="px-3 py-1.5 text-zinc-300">{r.rx_mhz} / {r.tx_mhz}</td>
                  <td className="px-3 py-1.5 text-zinc-400">{r.slot_channel_ids.join("&")}</td>
                  <td className={`px-3 py-1.5 ${r.pa_temp_c > 65 ? "text-red-300" : r.pa_temp_c > 50 ? "text-amber-300" : "text-emerald-300"}`}>
                    {r.pa_temp_c.toFixed(1)}
                  </td>
                  <td className="px-3 py-1.5">{r.vswr.toFixed(2)}</td>
                  <td className="px-3 py-1.5">
                    <StatusBadge label={r.online ? "online" : "offline"} tone={r.online ? "ok" : "err"} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3">
        <InfoCard label="Antennas indoor" value={String(site.antennas.indoor)} sub="across P1-P4" />
        <InfoCard label="Antennas outdoor" value={String(site.antennas.outdoor)} sub="rooftop + perimeter" />
        <InfoCard label="Antennas total" value={String(site.antennas.total)} sub="STS 460 DMR + PCPI" />
      </div>

      <div className="grid lg:grid-cols-2 gap-3">
        <MockLossBudget />
        <MockSshTerminal />
      </div>
    </div>
  );
}

function InfoCard({ label, value, sub }: { label: string; value: string; sub: string }) {
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-3">
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-2xl font-mono font-semibold text-zinc-100 mt-1">{value}</div>
      <div className="text-[10px] text-zinc-500 mt-0.5">{sub}</div>
    </div>
  );
}

function CodeplugsTab() {
  const site = PAD2;
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 overflow-hidden">
        <div className="px-3 py-2 text-xs font-mono uppercase tracking-widest text-zinc-400 border-b border-zinc-800">
          Codeplug Versions · git-style immutable history
        </div>
        <ol className="divide-y divide-zinc-900">
          {site.codeplugs.map((cp) => (
            <li key={cp.version} className="px-3 py-2 flex items-baseline gap-3 text-xs">
              <span className="font-mono font-semibold text-emerald-300 w-16">{cp.version}</span>
              <span className="font-mono text-zinc-500 w-28">{cp.committed_at}</span>
              <span className="font-mono text-zinc-500 w-40 truncate">{cp.committed_by}</span>
              <span className="flex-1 text-zinc-300 truncate">{cp.message}</span>
              <span className="flex gap-1.5 shrink-0">
                {cp.is_standard && <StatusBadge label="standard" tone="info" />}
                {cp.is_deployed && <StatusBadge label="deployed" tone="ok" />}
              </span>
            </li>
          ))}
        </ol>
      </div>
      <div className="grid lg:grid-cols-2 gap-3">
        <MockCodeplugDiff />
        <MockR7Provision />
      </div>
    </div>
  );
}

function FrequenciesTab() {
  const site = PAD2;
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 overflow-hidden">
        <div className="px-3 py-2 text-xs font-mono uppercase tracking-widest text-zinc-400 border-b border-zinc-800">
          Frequency Allocations · BNetzA · {site.frequencies.length} channels
        </div>
        <table className="w-full text-xs">
          <thead className="bg-zinc-950/60 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
            <tr>
              <th className="text-left px-3 py-2">Rx (MHz)</th>
              <th className="text-left px-3 py-2">Tx (MHz)</th>
              <th className="text-left px-3 py-2">BW</th>
              <th className="text-left px-3 py-2">Authority</th>
              <th className="text-left px-3 py-2">Reference</th>
              <th className="text-left px-3 py-2">Valid until</th>
              <th className="text-left px-3 py-2">Bearer</th>
              <th className="text-left px-3 py-2">€/year</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-900 font-mono">
            {site.frequencies.map((f) => (
              <tr key={f.id}>
                <td className="px-3 py-1.5 text-emerald-300">{f.rx}</td>
                <td className="px-3 py-1.5 text-emerald-300">{f.tx}</td>
                <td className="px-3 py-1.5 text-zinc-400">{f.bandwidth_khz} kHz</td>
                <td className="px-3 py-1.5 text-zinc-400">{f.authority}</td>
                <td className="px-3 py-1.5 text-zinc-500">{f.ref}</td>
                <td className="px-3 py-1.5 text-zinc-300">{f.valid_until}</td>
                <td className="px-3 py-1.5 text-zinc-400">{f.cost_bearer}</td>
                <td className="px-3 py-1.5 text-zinc-300">{f.annual_cost_eur}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="grid lg:grid-cols-2 gap-3">
        <MockComplianceCal />
        <MockSelfService />
      </div>
    </div>
  );
}

function SecretsTab() {
  const site = PAD2;
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 overflow-hidden">
        <div className="px-3 py-2 text-xs font-mono uppercase tracking-widest text-zinc-400 border-b border-zinc-800 flex items-center gap-2">
          <Shield className="h-3.5 w-3.5 text-amber-400" />
          Secret Vault · envelope-encrypted via AWS KMS · reveal triggers audit + Mattermost
        </div>
        <table className="w-full text-xs">
          <thead className="bg-zinc-950/60 text-[10px] uppercase tracking-widest text-zinc-500 font-mono">
            <tr>
              <th className="text-left px-3 py-2">Label</th>
              <th className="text-left px-3 py-2">Type</th>
              <th className="text-left px-3 py-2">Last rotated</th>
              <th className="text-left px-3 py-2">Rotation due</th>
              <th className="text-left px-3 py-2">Revealed</th>
              <th className="text-left px-3 py-2">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-zinc-900 font-mono">
            {site.secrets.map((s, i) => {
              const overdue = s.rotation_due_at < "2026-05-21";
              return (
                <tr key={i}>
                  <td className="px-3 py-1.5 text-zinc-200">{s.label}</td>
                  <td className="px-3 py-1.5 text-zinc-400">{s.type}</td>
                  <td className="px-3 py-1.5 text-zinc-400">{s.last_rotated_at}</td>
                  <td className={`px-3 py-1.5 ${overdue ? "text-red-300" : "text-zinc-400"}`}>{s.rotation_due_at}</td>
                  <td className="px-3 py-1.5 text-zinc-500">{s.reveal_count}×</td>
                  <td className="px-3 py-1.5 flex gap-2">
                    <button className="text-[10px] text-emerald-300 hover:underline">reveal</button>
                    <button className="text-[10px] text-amber-300 hover:underline">rotate</button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="grid lg:grid-cols-2 gap-3">
        <MockSecretVault />
        <MockAuditPack />
      </div>
    </div>
  );
}

function HeatmapsTab() {
  return (
    <div className="grid lg:grid-cols-2 gap-3">
      <MockHeatmapViewer />
      <MockCoveragePredictor />
      <MockManDownGeofence />
    </div>
  );
}

function LayoutsTab() {
  const site = PAD2;
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 overflow-hidden">
        <div className="px-3 py-2 text-xs font-mono uppercase tracking-widest text-zinc-400 border-b border-zinc-800">
          AutoCAD Layouts · {site.documents.filter((d) => d.type === "autocad_layout").length} drawing(s)
        </div>
        <ul className="divide-y divide-zinc-900 text-xs">
          {site.documents
            .filter((d) => d.type === "autocad_layout" || d.type === "install_pdf")
            .map((d) => (
              <li key={d.name} className="px-3 py-1.5 flex items-baseline gap-2 font-mono">
                <FileText className="h-3 w-3 text-zinc-500" />
                <span className="text-zinc-200 flex-1 truncate">{d.name}</span>
                <span className="text-zinc-600">{(d.size_kb / 1024).toFixed(1)} MB</span>
                <button className="text-emerald-300 hover:underline">view</button>
                <button className="text-zinc-400 hover:underline">download</button>
              </li>
            ))}
        </ul>
      </div>
      <div className="grid lg:grid-cols-2 gap-3">
        <MockLossBudget />
        <MockCoveragePredictor />
      </div>
    </div>
  );
}

function PhotosTab() {
  return (
    <div className="grid lg:grid-cols-2 gap-3">
      <MockFieldPhotos />
      <MockSpotifyWrapped />
      <MockSiteComparison />
    </div>
  );
}

function ActivityTab() {
  return (
    <div className="grid lg:grid-cols-2 gap-3">
      <MockLogTail />
      <MockSlackBot />
    </div>
  );
}

function CrewTab() {
  const site = PAD2;
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-zinc-800 bg-zinc-900/40 overflow-hidden">
        <div className="px-3 py-2 text-xs font-mono uppercase tracking-widest text-zinc-400 border-b border-zinc-800">
          Crew Log · field-visit history
        </div>
        <ol className="divide-y divide-zinc-900">
          {site.crew_log.map((c, i) => (
            <li key={i} className="px-3 py-2 flex items-baseline gap-3 text-xs font-mono">
              <span className="text-zinc-500 w-24">{c.date}</span>
              <span className="text-zinc-300 w-40 truncate">{c.visitor}</span>
              <span className={`w-28 ${c.activity === "install" ? "text-emerald-300" : c.activity === "maintenance" ? "text-amber-300" : "text-sky-300"}`}>
                {c.activity}
              </span>
              <span className="flex-1 text-zinc-400 truncate">{c.summary}</span>
              <span className="text-zinc-600">{c.duration_hours}h</span>
            </li>
          ))}
        </ol>
      </div>
      <MockCrewTracking />
    </div>
  );
}

function NonPad2Placeholder({ code }: { code: string }) {
  return (
    <div className="p-6 sm:p-10 max-w-3xl">
      <Link to="/" className="text-xs font-mono text-zinc-500 hover:text-zinc-300">
        ← all sites
      </Link>
      <h1 className="text-2xl font-bold font-mono mt-4">{code}</h1>
      <p className="text-zinc-400 mt-3 text-sm">
        Site-Detail-View is wired to API <code className="font-mono text-emerald-300">/api/v1/dossier/sites/{code}</code> —
        in MVP only <code className="font-mono">PAD2</code> is statically pre-populated as a
        reference site. Other sites become live as the backend gets fed (CSV-import seeded 234
        sites at structure-level; per-site detail-records follow in Phase 1).
      </p>
      <Link
        to="/sites/PAD2"
        className="inline-block mt-6 px-3 py-2 text-sm font-mono border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 rounded hover:bg-emerald-500/20"
      >
        See PAD2 reference view →
      </Link>
    </div>
  );
}
